import { useState, useEffect } from 'react';

export interface BotDetectionResult {
  isBot: boolean;
  confidence: number; // 0-1, higher means more likely to be a bot
  reasons: string[];
}

export const useBotDetection = (): BotDetectionResult => {
  const [result, setResult] = useState<BotDetectionResult>({
    isBot: false,
    confidence: 0,
    reasons: []
  });

  useEffect(() => {
    // Only run bot detection if enabled
    const isEnabled = import.meta.env.VITE_CHAT_ENABLE_BOT_DETECTION === 'true';
    
    if (!isEnabled) {
      setResult({
        isBot: false,
        confidence: 0,
        reasons: ['Bot detection disabled']
      });
      return;
    }

    const detectBot = () => {
      const checks: { [key: string]: boolean } = {};
      const reasons: string[] = [];

      // Only check for very obvious bot indicators
      
      // Check 1: WebDriver detection (very reliable)
      if (navigator.webdriver) {
        checks.webdriver = true;
        reasons.push('WebDriver detected');
      }

      // Check 2: Very obvious bot user agents
      const userAgent = navigator.userAgent.toLowerCase();
      const obviousBotPatterns = [
        /headless/i, /phantom/i, /selenium/i, /puppeteer/i, /playwright/i
      ];
      
      if (obviousBotPatterns.some(pattern => pattern.test(userAgent))) {
        checks.userAgent = true;
        reasons.push('Automation tool detected');
      }

      // Check 3: Missing essential browser features
      if (!navigator.languages || navigator.languages.length === 0) {
        checks.languages = true;
        reasons.push('No languages detected');
      }

      // Check 4: Headless browser detection (very reliable)
      if (window.outerHeight === 0 || window.outerWidth === 0) {
        checks.headless = true;
        reasons.push('Headless browser detected');
      }

      // Check 5: Phantom JS detection (very specific)
      if ((window as any)._phantom || (window as any).phantom) {
        checks.phantom = true;
        reasons.push('PhantomJS detected');
      }

      // Calculate confidence score - only count very reliable indicators
      const totalChecks = Object.keys(checks).length;
      const confidence = totalChecks > 0 ? totalChecks / 5 : 0; // Out of 5 reliable checks

      // Only consider it a bot if we have very strong evidence (60% threshold)
      const isBot = confidence >= 0.6;

      setResult({
        isBot,
        confidence,
        reasons: reasons.length > 0 ? reasons : ['Human-like behavior detected']
      });
    };

    // Run detection after a short delay to allow page to fully load
    const timer = setTimeout(detectBot, 500);

    return () => clearTimeout(timer);
  }, []);

  return result;
};
